/** The page's handle on the converter worker, and the operations an upload needs. */
import { DeclaredCommandT } from "../../flatbuffers/generated/mce/declared-command";
import { DiagnosticCode } from "../../flatbuffers/generated/mce/diagnostic-code";
import { Severity } from "../../flatbuffers/generated/mce/severity";
import { MappingT } from "../../flatbuffers/generated/mce/mapping";
import { MappingOverridesT } from "../../flatbuffers/generated/mce/mapping-overrides";
import { ProductType } from "../../flatbuffers/generated/mce/product-type";
import { TranslationTableT } from "../../flatbuffers/generated/mce/translation-table";
import { IdlResultT } from "../../flatbuffers/generated/mce/idl-result";
import { MceIdlHostTrap } from "./abi";
import { MceIdlSkew } from "./engine";
import type { MceIdlWorkerReply } from "./mceIdl.worker";
import {
  MceIdlSources,
  packBuildRequest,
  packBuildRequestFromMapping,
  packInflateRequest,
  packValidateRequest,
  unmatchableCommandNames,
  unpackResult,
  unpackTranslationTable,
} from "./messages";
import { unpackOverrides } from "./overrides";

/** A converter call longer than the server's own deadline has hung. */
const CALL_TIMEOUT_MS = 120000;

interface PendingCall {
  resolve: (answer: Uint8Array) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

let worker: Promise<Worker> | null = null;
let nextId = 1;
const pending = new Map<number, PendingCall>();

function failureFrom(reply: MceIdlWorkerReply): Error {
  if (reply.errorName === "MceIdlSkew") return new MceIdlSkew(reply.errorMessage ?? "");
  if (reply.errorName === "MceIdlHostTrap") return new MceIdlHostTrap(reply.errorMessage ?? "");
  return new Error(reply.errorMessage ?? "The MCE IDL converter failed");
}

/** Fail every call in flight and drop the worker, so the next call starts a new one. */
function abandonWorker(running: Worker | null, reason: string): void {
  running?.terminate();
  worker = null;
  const calls = [...pending.values()];
  pending.clear();
  calls.forEach((one) => {
    clearTimeout(one.timer);
    one.reject(new Error(reason));
  });
}

function workerNow(): Promise<Worker> {
  if (!worker) {
    worker = import("./spawnWorker")
      .then((spawn) => {
        const running = spawn.default();
        running.addEventListener("message", (event: MessageEvent<MceIdlWorkerReply>) => {
          const one = pending.get(event.data.id);
          if (!one) return;
          pending.delete(event.data.id);
          clearTimeout(one.timer);
          if (event.data.answer) one.resolve(event.data.answer);
          else one.reject(failureFrom(event.data));
        });
        running.addEventListener("error", (event: ErrorEvent) =>
          abandonWorker(running, `The MCE IDL converter stopped: ${event.message}`),
        );
        running.addEventListener("messageerror", () =>
          abandonWorker(running, "The MCE IDL converter sent an unreadable answer"),
        );
        return running;
      })
      .catch((error) => {
        // A failed chunk fetch must not break every later call until a reload.
        worker = null;
        throw error;
      });
  }
  return worker;
}

async function call(request: Uint8Array): Promise<Uint8Array> {
  const running = await workerNow();
  const id = nextId;
  nextId += 1;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => abandonWorker(running, "The MCE IDL converter did not answer in time"),
      CALL_TIMEOUT_MS,
    );
    pending.set(id, { resolve, reject, timer });
    try {
      running.postMessage({ id, request }, [request.buffer]);
    } catch (error) {
      // The worker is fine; this one request never reached it.
      pending.delete(id);
      clearTimeout(timer);
      reject(error);
    }
  });
}

/** Check the files without building. */
export async function validateMceIdl(
  product: ProductType | null,
  sources: MceIdlSources,
): Promise<IdlResultT> {
  return unpackResult(await call(packValidateRequest(product, sources)));
}

/** Check the files and build their Mapping. */
export async function buildMceIdl(
  product: ProductType,
  sources: MceIdlSources,
  overrides: MappingOverridesT | null = null,
): Promise<IdlResultT> {
  return unpackResult(await call(packBuildRequest(product, sources, overrides)));
}

/** The translation table a built or stored Mapping carries. */
export async function readTranslationTable(mapping: MappingT): Promise<TranslationTableT | null> {
  if (!mapping.translation) return null;
  const result = unpackResult(await call(packInflateRequest(mapping.translation)));
  return unpackTranslationTable(Uint8Array.from(result.inflated));
}

/** The overrides a stored Mapping carries, or an empty document. */
export async function readOverrides(mapping: MappingT): Promise<MappingOverridesT> {
  if (!mapping.overrides) return new MappingOverridesT();
  const result = unpackResult(await call(packInflateRequest(mapping.overrides)));
  return unpackOverrides(Uint8Array.from(result.inflated));
}

/** Check and build a stored Mapping's inputs with other overrides. */
export async function rebuildWithOverrides(
  mapping: MappingT,
  overrides: MappingOverridesT,
): Promise<IdlResultT> {
  return unpackResult(await call(packBuildRequestFromMapping(mapping, overrides)));
}

/** The commands an IDL declares, as the override editor lists them. */
export interface MceDeclaredCommands {
  commands: DeclaredCommandT[];
  /** Names of the commands whose signature the converter could not match. */
  unmatchable: Set<string>;
  /** The converter stopped listing at its size limit, so some are missing. */
  cutOff: boolean;
}

/** The commands the mapping's IDL declares. Empty when this converter does not report them. */
export async function declaredCommands(
  mapping: MappingT,
  overrides: MappingOverridesT,
): Promise<MceDeclaredCommands> {
  const answer = await call(packBuildRequestFromMapping(mapping, overrides));
  const result = unpackResult(answer);
  return {
    commands: result.commands,
    unmatchable: unmatchableCommandNames(answer),
    cutOff: result.diagnostics.some(
      (finding) =>
        finding.code === DiagnosticCode.TooLarge && finding.severity === Severity.Warning,
    ),
  };
}
